import { createRoute, lazyRouteComponent } from "@tanstack/react-router";

import { NestedRouteErrorComponent } from "@/components/organisms/ErrorBoundary/NestedRouteErrorComponent";
import {
  type DevicesView,
  isDevicesView,
} from "@/features/devices/devicesView";
import type { DeviceType } from "@/features/devices/deviceType";
import { DEVICE_TYPES } from "@/features/devices/deviceTypes";
import { plantsRoute } from "@/pages/Plants/route";

interface PlantDetailSearch {
  tab?: DeviceType;
  available?: boolean;
  view?: DevicesView;
}

// Contenedor transparente (sin component: renderiza solo el Outlet del hijo).
// Aporta el segmento `$plantId` y el crumb con el nombre de la planta, y sirve
// de raíz común de las pestañas de planta y de los detalles de dispositivo.
export const plantDetailRoute = createRoute({
  path: "$plantId",
  getParentRoute: () => plantsRoute,
  staticData: { crumb: "plantName" },
});

// Layout pathless (id sin path): envuelve las pestañas de planta con la cabecera
// y las tabs, sin arrastrar ese chrome a los detalles de dispositivo (que cuelgan
// directamente de plantDetailRoute como hermanos de esta layout). Aloja también
// el validateSearch de tab/available para que no lo hereden NCU/TCU/HSU.
export const plantDetailLayoutRoute = createRoute({
  id: "_plantDetail",
  getParentRoute: () => plantDetailRoute,
  component: lazyRouteComponent(async () => {
    const m = await import("./PlantDetail");
    return { default: m.PlantDetail };
  }),
  validateSearch: (search: Record<string, unknown>): PlantDetailSearch => {
    const tab = search.tab as DeviceType | undefined;
    const isKnownTab =
      tab === DEVICE_TYPES.NCU ||
      tab === DEVICE_TYPES.TCU ||
      tab === DEVICE_TYPES.HSU;
    return {
      tab: isKnownTab ? tab : undefined,
      available: search.available as boolean | undefined,
      // Sin valor se queda undefined: la vista por defecto la decide DevicesPage según
      // haya o no datos de layout.
      view: isDevicesView(search.view) ? search.view : undefined,
    };
  },
});

export const plantDetailIndexRoute = createRoute({
  path: "/",
  getParentRoute: () => plantDetailLayoutRoute,
  component: lazyRouteComponent(async () => {
    const m = await import("./DevicesPage/DevicesPage");
    return { default: m.DevicesPage };
  }),
  errorComponent: NestedRouteErrorComponent,
});
